import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { SuccessAlertContext } from "App";
import { IncrementalSearchElement, SelectForm } from "./Common";
import { extractNoDupplicationSupplier, removeEmptyValues } from "../utils/common";
import CustomTextField from "./CustomTextField";
import Surround from "./Surround";
import {
  GeneralRepresentativeSearchWithBlank,
  OrderLargeClassification,
  ProductCdSearchParam,
  Subclass,
  TyreOptions,
  TyreWheelRepresentativeSearchWithBlank,
  WheelOptions,
} from "../types";
import { productTypeOptions, tyreWheelRepresentativeSearchOptions, generalRepresentativeSearchOptions } from "../constants";
import useProductCdSearch from "../hooks/useProductCdSearch";

type Props = {
  isOpenProductCdSearch: boolean;
  setIsOpenProductCdSearch: Dispatch<SetStateAction<boolean>>;
  selectedIndex: number | undefined;
  setSelectedIndex: Dispatch<SetStateAction<number | undefined>>;
  classificationOptions: OrderLargeClassification[];
  brandOptions: string[];
  tyreOptions: TyreOptions;
  wheelOptions: WheelOptions;
  setProductCd: (productCd: string, selectedIndex: number) => void;
  productType: (typeof productTypeOptions)[number];
  clientCd?: string;
};

const incrementalSearchOptionLength = 10;

const styles: { [key: string]: SxProps } = {
  searchRowBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
};

/**
 * 商品コード検索モーダル
 * @param root0 商品コード検索モーダルのプロップス
 */
export default function ProductCdSearch({
  isOpenProductCdSearch,
  setIsOpenProductCdSearch,
  selectedIndex,
  setSelectedIndex,
  classificationOptions,
  brandOptions,
  tyreOptions,
  wheelOptions,
  setProductCd,
  productType,
  clientCd,
}: Props) {
  const [selectedProductCdIndex, setSelectedProductCdIndex] = useState<number | undefined>(
    undefined,
  );
  const [
    {
      productName,
      brand,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      supplier,
      tyreWheelRepresentativeCd,
      generalRepresentativeCd,
      productInfoList,
      subclass,
    },
    {
      setProductName,
      setBrand,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setSupplier,
      setTyreWheelRepresentativeCd,
      setGeneralRepresentativeCd,
      setProductInfoList,
      setSubclass,
      get,
    },
    { isLoading },
  ] = useProductCdSearch(clientCd);
  const { showAlert } = useContext(SuccessAlertContext);


  const tyreWheelClasses: {[K in typeof tyreWheelRepresentativeCd]?: string} = {
    "タイヤ": "02",
    "ホイール": "03",
    "代表コード": classification ?? undefined
  };
  const generalClasses: {[K in typeof generalRepresentativeCd]?: string} = {
    "A・V・C": "04",
    "エンジンオイル": "05",
    "四輪車バッテリー": "06",
    "手入れ・洗車・ケミカル": "07",
    "車内用品": "08",
    "車外用品": "09",
    "機能用品": "10",
    "代表コード": classification ?? undefined
  };

  const tyreWheelClassCd = tyreWheelClasses[tyreWheelRepresentativeCd];
  const generalClassCd = generalClasses[generalRepresentativeCd];
  const createSearchParams = (): ProductCdSearchParam => {
    const params = {
      limit: 100,
      client_cd: clientCd,
      item_name: productName,
      item_num: "",
      brand,
      class_cd: (productType === "タイヤ・ホイール" ? tyreWheelClassCd : generalClassCd),
      subclass_cd: subclass,
      vendor_cd: supplier
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "",
      tire_width: (productType === "タイヤ・ホイール" && tyreWheelClassCd === "02") ? tyreWidth : undefined,
      tire_ratio: (productType === "タイヤ・ホイール" && tyreWheelClassCd === "02") ? tyreRatio : undefined,
      tire_rim_size: (productType === "タイヤ・ホイール" && tyreWheelClassCd === "02") ? tyreRimDiam : undefined,
      wheel_width: (productType === "タイヤ・ホイール" && tyreWheelClassCd === "03") ? wheelWidth : undefined,
      wheel_pcd: (productType === "タイヤ・ホイール" && tyreWheelClassCd === "03") ? wheelPcd : undefined,
      wheel_hole: (productType === "タイヤ・ホイール" && tyreWheelClassCd === "03") ? wheelHole : undefined,
      wheel_rim_size: (productType === "タイヤ・ホイール" && tyreWheelClassCd === "03") ? wheelRimDiam : undefined,
    };
    return removeEmptyValues(params, ["limit"]);
  };

  const resetValues = () => {
    setProductInfoList([]);
    setTyreWheelRepresentativeCd("");
    setGeneralRepresentativeCd("");
    setProductName("");
    setBrand("");
    setTyreWidth(undefined);
    setTyreRatio(undefined);
    setTyreRimDiam(undefined);
    setWheelWidth(undefined);
    setWheelPcd(undefined);
    setWheelHole(undefined);
    setWheelRimDiam(undefined);
    setClassification("");
    setSubclass("");
    setSupplier("");
    setSelectedIndex(undefined);
    setSelectedProductCdIndex(undefined);
  };

  const handleClose = () => {
    setIsOpenProductCdSearch(false);
    resetValues();
  };

  const handleSearch = () => {
    // タイヤ/ホイール/代表コード、一般用品/代表コードを選べていない場合はエラー
    if (productType === "タイヤ・ホイール" && !tyreWheelRepresentativeCd) {
      showAlert('タイヤ/ホイール/代表コードを選択してください。', 'error');
      return;
    }
    if (productType === "一般用品" && !generalRepresentativeCd) {
      showAlert('一般用品/代表コードを選択してください。', 'error');
      return;
    }
    get(createSearchParams());
  };

  // 大分類から仕入先を探す
  const calcSupplierOptions = () => (
    // let sOptions: string[] = [];
    // if (classification) {
    //   // 分類の中に仕入先のみ候補にする
    //   const sList = classificationOptions.filter((c) => c.name === classification)[0].suppliers;
    //   sOptions = sList ? sList.map((s) => s.supplierName) : [];
    // } else
    //   sOptions = extractNoDupplicationSupplier(classificationOptions).map((so) => so.supplierName); // 全仕入先を候補にする
    // return sOptions;
     extractNoDupplicationSupplier(classificationOptions).map((so) => so.supplierName) // 全仕入先を候補にする
  );

  // 大分類から中分類を探す
  const calcSubclassOptions = () => {
    let sOptions: string[] = [];
    if (classification) {
      // 分類の中に仕入先のみ候補にする
      const sList = classificationOptions.find((c) => c.name === classification)?.subclass;
      sOptions = sList ? sList.map((s) => s.subclassName) : [];
    } else {
      const scOptions = classificationOptions
        .map((co) => co.subclass)
        .flat()
        .filter((so) => so != null) as Subclass[];
      sOptions = scOptions.map((so) => so.subclassName); // 全中分類を候補にする
    }
    return sOptions;
  };

  // 適用
  const handleApply = () => {
    if (productType === "タイヤ・ホイール") {
      if (tyreWheelRepresentativeCd === "代表コード") {
        // 95の代表コードを大分類、中分類に基づいて入れ、6桁にする。
        const clao = classificationOptions.find(
          (co) => co.name === classification,
        ) as OrderLargeClassification;
        const subo = clao.subclass
          ? (clao.subclass.find((co) => co.subclassName === subclass)?.subclassCd ?? "00")
          : "00";
        if (selectedIndex != null) setProductCd(`95${clao.id}${subo}`, selectedIndex);
        setIsOpenProductCdSearch(false);
        resetValues();
      } else {
        // 代表コード以外の場合は選択されている商品コードをセットする
        if (selectedProductCdIndex != null && selectedIndex != null)
          setProductCd(productInfoList[selectedProductCdIndex].productCd, selectedIndex);
        setIsOpenProductCdSearch(false);
        resetValues();
      }
    } else {
      // 一般用品
      // eslint-disable-next-line no-lonely-if
      if (generalRepresentativeCd === "代表コード") {
        // 95の代表コードを大分類、中分類に基づいて入れ、6桁にする。
        const clao = classificationOptions.find(
          (co) => co.name === classification,
        ) as OrderLargeClassification;
        const subo = clao.subclass
          ? (clao.subclass.find((co) => co.subclassName === subclass)?.subclassCd ?? "00")
          : "00";
        if (selectedIndex != null) setProductCd(`95${clao.id}${subo}`, selectedIndex);
        setIsOpenProductCdSearch(false);
        resetValues();
      } else {
        // 代表コード以外の場合は選択されている商品コードをセットする
        if (selectedProductCdIndex != null && selectedIndex != null)
          setProductCd(productInfoList[selectedProductCdIndex].productCd, selectedIndex);
        setIsOpenProductCdSearch(false);
        resetValues();
      }
    }
  };

  return (
    <Dialog
      open={isOpenProductCdSearch}
      onClose={() => {
        handleClose();
      }}
      PaperProps={{ sx: { position: "relative", maxWidth: 800 } }}
    >
      <DialogTitle>商品コード検索</DialogTitle>
      <DialogContent sx={{pr: 0}}>
        { productType === "タイヤ・ホイール" &&
          <Box display="flex" flexDirection="row" justifyContent="space-around">
            <Box textAlign="center" justifyItems="center" mt={2} mb={1} width="380px">
              <SelectForm
                key="tyre_wheel_representative_cd"
                id="tyre_wheel_representative_cd"
                label="タイヤ/ホイール/代表コード"
                optionList={tyreWheelRepresentativeSearchOptions}
                value={tyreWheelRepresentativeCd}
                setValue={(val: TyreWheelRepresentativeSearchWithBlank) => {
                  setTyreWheelRepresentativeCd(val);
                }}
                required
                fullWidth
                minWidth={80}
                isContainBlank
              />
              {tyreWheelRepresentativeCd !== "代表コード" && (
                <>
                  <Box sx={styles.searchRowBox}>
                    <IncrementalSearchElement
                      key="supplier_name"
                      label="仕入先名"
                      options={calcSupplierOptions()}
                      value={supplier}
                      onChange={(val: string) => setSupplier(val)}
                      optionLength={incrementalSearchOptionLength}
                      isBlankNoOptions
                    />
                    <IncrementalSearchElement
                      key="brand"
                      label="ブランド"
                      options={brandOptions}
                      value={brand}
                      onChange={(val: string) => setBrand(val)}
                      optionLength={incrementalSearchOptionLength}
                    />
                  </Box>
                  <Box mt={1} width="100%">
                    <CustomTextField
                      id="product_name"
                      text={productName}
                      label="ｼｮｳﾋﾝﾒｲ(半角ｶﾅ)"
                      setText={(val: string) => setProductName(val)}
                      fullWidth
                      maxWidth="calc(100% - 16px)"
                      sx={{ mx: 1 }}
                    />
                  </Box>
                </>
              )}
              <>
                <Box height={8} />
                {tyreWheelRepresentativeCd === "タイヤ" && (
                  <Surround title="タイヤサイズ">
                    <Box marginTop={0.5} />
                    <Box sx={styles.searchRowBox}>
                      <SelectForm
                        id="tyre-width"
                        label="幅"
                        optionList={tyreOptions.tyreWidthList.map((t) => `${t}`)}
                        value={`${tyreWidth ?? ""}`}
                        setValue={(val) => {
                          setTyreWidth(val === "" ? undefined : Number(val));
                        }}
                        isContainBlank
                      />
                      <SelectForm
                        id="tyre-ratio"
                        label="偏平率"
                        optionList={tyreOptions.tyreRatioList.map((t) => `${t}`)}
                        value={`${tyreRatio ?? ""}`}
                        setValue={(val) => {
                          setTyreRatio(val === "" ? undefined : Number(val));
                        }}
                        isContainBlank
                      />
                    </Box>
                    <Box sx={styles.searchRowBox}>
                      <SelectForm
                        id="tyre-rim-diam"
                        label="タイヤリム径"
                        optionList={tyreOptions.tyreRimDiamList.map((t) => `${t}`)}
                        value={`${tyreRimDiam ?? ""}`}
                        setValue={(val) => {
                          setTyreRimDiam(val === "" ? undefined : Number(val));
                        }}
                        isContainBlank
                      />
                    </Box>
                  </Surround>
                )}
                {tyreWheelRepresentativeCd === "ホイール" && (
                  <Surround title="ホイールサイズ">
                    <Box marginTop={0.5} />
                    <Box sx={styles.searchRowBox}>
                      <SelectForm
                        id="wheel-rim-diam"
                        label="ホイールリム径"
                        optionList={wheelOptions.wheelRimDiamList.map((t) => `${t}`)}
                        value={`${wheelRimDiam ?? ""}`}
                        setValue={(val) => {
                          setWheelRimDiam(val === "" ? undefined : Number(val));
                        }}
                        isContainBlank
                      />
                      <SelectForm
                        id="wheel-width"
                        label="リム幅"
                        optionList={wheelOptions.wheelWidthList.map((t) => `${t}`)}
                        value={`${wheelWidth ?? ""}`}
                        setValue={(val) => {
                          setWheelWidth(val === "" ? undefined : Number(val));
                        }}
                        isContainBlank
                      />
                    </Box>
                    <Box sx={styles.searchRowBox}>
                      <SelectForm
                        id="wheel-hole"
                        label="ホール数"
                        optionList={wheelOptions.wheelHoleList.map((t) => `${t}`)}
                        value={`${wheelHole ?? ""}`}
                        setValue={(val) => {
                          setWheelHole(val === "" ? undefined : Number(val));
                        }}
                        isContainBlank
                      />
                      <SelectForm
                        id="wheel-pcd"
                        label="PCD"
                        optionList={wheelOptions.wheelPcdList.map((t) => `${t.toFixed(2)}`)}
                        value={`${wheelPcd ? wheelPcd.toFixed(2) : ""}`}
                        setValue={(val) => {
                          setWheelPcd(val === "" ? undefined : Number(val));
                        }}
                        isContainBlank
                      />
                    </Box>
                  </Surround>
                )}
                {tyreWheelRepresentativeCd === "代表コード" && (
                  <Surround title="代表コード">
                    <Box marginTop={0.5} />
                    <Box sx={{...styles.searchRowBox, ...{width: 360}}}>
                      <SelectForm
                        id="classification"
                        label="大分類"
                        optionList={classificationOptions.map((co) => co.name).filter(cName => (cName === "タイヤ" || cName === "ホイール"))}
                        value={classification}
                        setValue={(val) => {
                          setClassification(val);
                        }}
                        isContainBlank
                      />
                      <SelectForm
                        id="subclass"
                        label="中分類"
                        optionList={calcSubclassOptions()}
                        value={subclass}
                        setValue={(val) => {
                          setSubclass(val);
                        }}
                        isContainBlank
                      />
                    </Box>
                  </Surround>
                )}
              </>
              {tyreWheelRepresentativeCd !== "代表コード" && (
                <Box justifySelf="right" m={2}>
                  <Button variant="contained" onClick={() => handleSearch()}>
                    検索
                  </Button>
                </Box>
              )}
            </Box>
            {tyreWheelRepresentativeCd !== "代表コード" && (
              <Box p={2} borderLeft="1px solid rgba(220,220,220,0.3)">
                <Box my={1}>
                  <Typography textAlign="center">該当する商品コード</Typography>
                </Box>
                <Box sx={{
                  overflowY: 'auto',
                  width: '250px',
                  height: '300px',
                }}>
                  <List disablePadding>
                    {productInfoList.length > 0 &&
                      productInfoList.map((pInfo, index) => (
                        <ListItemButton
                          key={pInfo.productCd}
                          selected={selectedProductCdIndex === index}
                          onClick={() => setSelectedProductCdIndex(index)}
                        >
                          <ListItemText primary={`${pInfo.productCd} ${pInfo.productName} ${pInfo.size}`} />
                        </ListItemButton>
                      ))}
                  </List>
                </Box>
              </Box>
            )}
          </Box>
        }
        { productType === "一般用品" &&
          <Box display="flex" flexDirection="row" justifyContent="space-around">
            <Box textAlign="center" justifyItems="center" mt={2} mb={1} width="380px">
              <SelectForm
                key="general_representative_cd"
                id="general_representative_cd"
                label="一般用品/代表コード"
                optionList={generalRepresentativeSearchOptions}
                value={generalRepresentativeCd}
                setValue={(val: GeneralRepresentativeSearchWithBlank) => {
                  setGeneralRepresentativeCd(val);
                }}
                required
                fullWidth
                minWidth={80}
                isContainBlank
              />
              {generalRepresentativeCd !== "代表コード" && (
                <>
                  <Box sx={styles.searchRowBox}>
                    <IncrementalSearchElement
                      key="supplier_name"
                      label="仕入先名"
                      options={calcSupplierOptions()}
                      value={supplier}
                      onChange={(val: string) => setSupplier(val)}
                      optionLength={incrementalSearchOptionLength}
                      maxWidth={380}
                      fullWidth
                      isBlankNoOptions
                    />
                  </Box>
                  <Box mt={1} width="100%">
                    <CustomTextField
                      id="product_name"
                      text={productName}
                      label="ｼｮｳﾋﾝﾒｲ(半角ｶﾅ)"
                      setText={(val: string) => setProductName(val)}
                      fullWidth
                      maxWidth="calc(100% - 16px)"
                      sx={{ mx: 1 }}
                    />
                  </Box>
                </>
              )}
              {generalRepresentativeCd === "代表コード" && (
                <Surround title="代表コード">
                  <Box marginTop={0.5} />
                  <Box sx={{...styles.searchRowBox, ...{width: 360}}}>
                    <SelectForm
                      id="classification"
                      label="大分類"
                      optionList={classificationOptions.map((co) => co.name).filter(cName => (cName !== "タイヤ" && cName !== "ホイール" && cName !== "組込済商品"))}
                      value={classification}
                      setValue={(val) => {
                        setClassification(val);
                      }}
                      isContainBlank
                    />
                    <SelectForm
                      id="subclass"
                      label="中分類"
                      optionList={calcSubclassOptions()}
                      value={subclass}
                      setValue={(val) => {
                        setSubclass(val);
                      }}
                      isContainBlank
                    />
                  </Box>
                </Surround>
              )}
              {generalRepresentativeCd !== "代表コード" && (
                <Box justifySelf="right" m={2}>
                  <Button variant="contained" onClick={() => handleSearch()}>
                    検索
                  </Button>
                </Box>
              )}
            </Box>
            {generalRepresentativeCd !== "代表コード" && (
              <Box p={2} borderLeft="1px solid rgba(220,220,220,0.3)">
                <Box my={1}>
                  <Typography textAlign="center">該当する商品コード</Typography>
                </Box>
                <Box sx={{
                  overflowY: 'auto',
                  width: '250px',
                  height: '300px',
                }}>
                  <List disablePadding>
                    {productInfoList.length > 0 &&
                      productInfoList.map((pInfo, index) => (
                        <ListItemButton
                          key={pInfo.productCd}
                          selected={selectedProductCdIndex === index}
                          onClick={() => setSelectedProductCdIndex(index)}
                        >
                          <ListItemText primary={`${pInfo.productCd} ${pInfo.productName} ${pInfo.size}`} />
                        </ListItemButton>
                      ))}
                  </List>
                </Box>
              </Box>
            )}
          </Box>
        }
      </DialogContent>
      <DialogActions>
        <Button variant="custom-outlined" onClick={() => handleClose()}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={() => handleApply()}
          disabled={
            (generalRepresentativeCd === "代表コード" || tyreWheelRepresentativeCd === "代表コード")
              ? !(classification.length > 0 && subclass.length > 0)
              : selectedProductCdIndex == null
          }
        >
          適用
        </Button>
      </DialogActions>
      {isLoading && ( // マスク
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.2)",
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
}
